'use client';

import { useCallback, useState } from 'react';
import { apiClient, ApiError } from '@/lib/apiClient';
import Cookies from 'js-cookie';

type ApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ApiOptions<TRequest extends object> {
  endpoint: string;
  method: ApiMethod;
  useRequestAs: 'body' | 'query';
  includeTpSessionId?: boolean;
  /**
   * Optional path variables to be replaced in the endpoint.
   * Path variables in the endpoint should be specified as {variableName}.
   * Example: '/users/{userId}/posts' with pathVariables: { userId: '123' }
   */
  pathVariables?: Record<string, string | number>;
}

export function useApiCall<TRequest extends object, TResponse>(options: ApiOptions<TRequest>) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Execute the API call
   * @param params - The request parameters
   * @param execOptions - Additional options for this specific execution
   * @returns The API response or null if an error occurred
   */
  const execute = useCallback(
    async (
      params: Omit<TRequest, 'tpSessionId'> & { tpSessionId?: string },
      execOptions?: {
        /**
         * Path variables to be replaced in the endpoint.
         * These will be merged with any path variables defined in the hook options.
         */
        pathVariables?: Record<string, string | number>;
      }
    ): Promise<TResponse | null> => {
      setIsLoading(true);
      setError(null);

      // Create a copy of params
      const requestParams: TRequest = {
        ...params,
        // Add tpSessionId if needed
        ...(options.includeTpSessionId
          ? {
              tpSessionId:
                params.tpSessionId ||
                Cookies.get(
                  process.env.NEXT_PUBLIC_TRAFFIC_SOURCES_COOKIE_NAME || 'trovanorma_tpSessionId'
                ) ||
                '',
            }
          : {}),
      } as TRequest;

      // Process endpoint with path variables if provided
      let processedEndpoint = options.endpoint;

      // Merge path variables from hook options and execution options
      const mergedPathVariables = {
        ...options.pathVariables,
        ...execOptions?.pathVariables,
      };

      // Replace path variables in the endpoint if any are provided
      if (Object.keys(mergedPathVariables).length > 0) {
        // Replace each path variable in the endpoint
        Object.entries(mergedPathVariables).forEach(([key, value]) => {
          const placeholder = `{${key}}`;
          processedEndpoint = processedEndpoint.replace(placeholder, String(value));
        });
      }

      try {
        return await apiClient<TRequest, TResponse>(processedEndpoint, {
          method: options.method,
          [options.useRequestAs]: requestParams,
        });
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred.');
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      options.includeTpSessionId,
      options.endpoint,
      options.pathVariables,
      options.method,
      options.useRequestAs,
    ]
  );

  return { execute, isLoading, error };
}
